import './week.css';

const reduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Max time a view transition waits for the app's async week load before giving up. */
const SWAP_TIMEOUT_MS = 1500;

type VTDocument = Document & { startViewTransition?: (cb: () => Promise<void>) => unknown };

export function init(root: HTMLElement): void {
  let lastWeek: Element | null = null;
  let frame = 0;

  /** Slide the pill under the current tab; scroll the strip so that tab is centred. */
  const sync = (): void => {
    frame = 0;
    const strip = root.querySelector<HTMLElement>('.week-strip');
    const pill = strip?.querySelector<HTMLElement>('.week-pill');
    const tab = strip?.querySelector<HTMLElement>('.week-tab[aria-current]');
    if (!strip || !pill || !tab) return;
    const { offsetLeft: x, offsetTop: y, offsetWidth: w, offsetHeight: h } = tab;
    pill.style.width = `${w}px`;
    pill.style.height = `${h}px`;
    pill.style.transform = `translate(${x}px, ${y}px)`;
    const ready = pill.hasAttribute('data-ready');
    if (!ready) {
      // First placement snaps; later ones glide.
      requestAnimationFrame(() => pill.setAttribute('data-ready', ''));
    }
    if (tab !== lastWeek) {
      lastWeek = tab;
      tab.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: reduced() || !ready ? 'auto' : 'smooth',
      });
    }
  };
  const schedule = (): void => {
    if (!frame) frame = requestAnimationFrame(sync);
  };

  new MutationObserver(schedule).observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-current'],
  });
  window.addEventListener('resize', schedule);
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(schedule).observe(root);
  void document.fonts?.ready.then(schedule);
  schedule();

  // Fade the games list across a week change. The app loads the week asynchronously,
  // so the transition's update step waits until the current tab changes (or times out).
  root.addEventListener(
    'click',
    (ev) => {
      const btn = (ev.target as Element).closest<HTMLElement>('[data-week]');
      if (!btn || btn.hasAttribute('aria-current') || reduced()) return;
      const vt = (document as VTDocument).startViewTransition?.bind(document);
      const swapped = (): Promise<void> =>
        new Promise((resolve) => {
          const done = (): void => {
            mo.disconnect();
            clearTimeout(timer);
            resolve();
          };
          const mo = new MutationObserver(() => {
            if (root.querySelector(`.week-tab[aria-current][data-week="${btn.dataset.week}"]`)) done();
          });
          const timer = setTimeout(done, SWAP_TIMEOUT_MS);
          mo.observe(root, { subtree: true, childList: true, attributes: true });
        });
      if (vt) {
        vt(swapped);
        return;
      }
      // Fallback: fade the new list in once the swap lands.
      void swapped().then(() => {
        const main = root.querySelector<HTMLElement>('.games-main');
        if (!main) return;
        main.classList.remove('games-main--in');
        void main.offsetWidth;
        main.classList.add('games-main--in');
      });
    },
    true,
  );
}
